// RNNoise: сырой трек микрофона → чистый трек для публикации. Портировано из
// демо-клиента: WASM и воркет лежат в public/rnnoise и грузятся один раз.
const ASSETS = ['/rnnoise/rnnoise-sync.js', '/rnnoise/rnnoise-worklet.js'];
const WORKLET_NAME = 'NoiseSuppressorWorklet';
// RNNoise приглушает сигнал — поднимаем обратно, а лимитер ловит пики
const MAKEUP_GAIN = 1.6;

// На iOS воркет глохнет после прерываний аудиосессии, там остается браузерное шумоподавление
export const IS_IOS =
  /iP(hone|ad|od)/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const RNNOISE_SUPPORTED = !IS_IOS && typeof AudioWorkletNode !== 'undefined';

// Склеенный код воркета: модуль регистрируется на контекст, код переживает его пересоздание
let workletCode: Promise<string> | null = null;

// Выход цепочки: трек в эфир (мьют глушит его) и тот же звук для индикатора уровня
export type CleanAudio = { track: MediaStreamTrack; meter: MediaStreamTrack };

export class NoiseSuppressor {
  private ctx: AudioContext | null = null;
  private loading: Promise<void> | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private node: AudioWorkletNode | null = null;

  // Контекст сменил состояние (запустился после клика, уснул): клиент ставит сюда проверку сторожа
  onState?: () => void;

  // Создать и разбудить контекст: вызывать синхронно из жеста пользователя,
  // иначе контекст родится suspended и в эфир уйдет тишина
  wake(): AudioContext | null {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext({ sampleRate: 48000 });
        this.ctx.onstatechange = () => this.onState?.();
      } catch {
        return null;
      }
    }
    if (this.ctx.state !== 'running') this.ctx.resume().catch(() => {});
    return this.ctx;
  }

  // Тяжелая загрузка (~1.9 МБ WASM): микрофон для нее не нужен, грузим заранее
  load(): Promise<void> {
    this.loading ??= this.addModule().catch((e) => {
      this.loading = null;
      throw e;
    });
    return this.loading;
  }

  private async addModule(): Promise<void> {
    const ctx = this.wake();
    if (!ctx) throw new Error('AudioContext недоступен');
    workletCode ??= Promise.all(ASSETS.map((url) => fetch(url).then((r) => r.text()))).then(
      // AudioWorklet не поддерживает importScripts — склеиваем в один модуль
      (parts) => parts.join('\n'),
    );
    const blobUrl = URL.createObjectURL(
      new Blob([await workletCode], { type: 'application/javascript' }),
    );
    try {
      await ctx.audioWorklet.addModule(blobUrl);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  }

  // Цепочка source → rnnoise → makeup → лимитер → destination; возвращает чистый моно-трек
  // и его отвод для индикатора уровня
  async attach(rawTrack: MediaStreamTrack): Promise<CleanAudio> {
    await this.load();
    // Без жеста контекст может остаться suspended: цепочку все равно собираем, трек в эфире
    // клиент замьютит до клика
    await this.tryResume(1500);
    const ctx = this.ctx!;
    this.detach();

    // Сырой трек на входе графа всегда включен: мьют выражается на чистом треке
    rawTrack.enabled = true;

    this.source = ctx.createMediaStreamSource(new MediaStream([rawTrack]));
    // Воркет считает только канал 0, а каналов на выходе отдает столько, сколько получил:
    // у двухканального микрофона канал 1 ушел бы в эфир тишиной. Вход не трогаем —
    // второй канал остается виден воркету
    this.node = new AudioWorkletNode(ctx, WORKLET_NAME, { outputChannelCount: [1] });
    const dest = ctx.createMediaStreamDestination();
    // Назначение по умолчанию двухканальное и апмиксит моно: LiveKit счел бы трек стерео
    // и публиковал стерео Opus без DTX и RED
    dest.channelCount = 1;
    // Отвод для индикатора уровня: мьют — это enabled трека в эфире, отвода он не касается
    const meter = ctx.createMediaStreamDestination();
    meter.channelCount = 1;
    const makeup = ctx.createGain();
    makeup.gain.value = MAKEUP_GAIN;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -3;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.1;

    this.source.connect(this.node);
    this.node.connect(makeup);
    makeup.connect(limiter);
    limiter.connect(dest);
    limiter.connect(meter);

    return { track: dest.stream.getAudioTracks()[0]!, meter: meter.stream.getAudioTracks()[0]! };
  }

  get isRunning(): boolean {
    return this.ctx?.state === 'running';
  }

  get isAttached(): boolean {
    return this.node !== null;
  }

  // Разбудить контекст и дождаться запуска: без жеста resume() не отказывает, а виснет,
  // поэтому ждем недолго и смотрим на состояние
  async tryResume(waitMs = 500): Promise<boolean> {
    const ctx = this.wake();
    if (!ctx) return false;
    if (ctx.state !== 'running') {
      await Promise.race([ctx.resume().catch(() => {}), new Promise((r) => setTimeout(r, waitMs))]);
    }
    return ctx === this.ctx && ctx.state === 'running';
  }

  // Жив ли контекст на самом деле: у зомби после прерывания аудиосессии state остается
  // running, а рендер-поток стоит и currentTime не растет — видно только замером
  async isFrozen(): Promise<boolean> {
    const ctx = this.ctx;
    if (!ctx) return false;
    if (ctx.state !== 'running') return true;
    const t0 = ctx.currentTime;
    await new Promise((r) => setTimeout(r, 250));
    return ctx === this.ctx && ctx.currentTime - t0 < 0.05;
  }

  // Цепочка заново на новом контексте: мертвый закрываем не дожидаясь, его close() может
  // не вернуться. Вне жеста новый контекст может родиться suspended — тогда чистый трек
  // молчит, пока wake() из клика его не разбудит. Код воркета в памяти, второй загрузки нет
  rebuild(rawTrack: MediaStreamTrack): Promise<CleanAudio> {
    this.detach();
    try {
      this.ctx?.close().catch(() => {});
    } catch {
      // мертвый контекст может бросить синхронно
    }
    this.ctx = null;
    this.loading = null;
    return this.attach(rawTrack);
  }

  destroy(): void {
    this.detach();
    this.ctx?.close().catch(() => {});
    this.ctx = null;
    this.loading = null;
  }

  private detach(): void {
    this.source?.disconnect();
    this.node?.disconnect();
    this.source = null;
    this.node = null;
  }
}
